import React from "react";
import useAuth from "../hooks/useAuth.js";
import { Link } from "react-router-dom";
import Navbar from "./Navbar.js";
import Footer from "./Footer.js";

const AboutUs = () => {
const { isAuthenticated } = useAuth();

  const stats = [
    { value: "5,000+", label: "Active Users" },
    { value: "$10M+", label: "Collective Savings" },
    { value: "1,000+", label: "Thrift Groups" },
    { value: "4", label: "Countries" },
  ];

  const visionPoints = [
    {
      icon: "🤝",
      title: "Community First",
      description: "We believe in the power of collective action and mutual support, building technology that strengthens community bonds."
    },
    {
      icon: "🔒",
      title: "Trust & Security",
      description: "Security is paramount. We've built robust systems to protect every transaction and keep your savings safe."
    },
    {
      icon: "🌍",
      title: "Financial Inclusion",
      description: "Making financial tools accessible to everyone, regardless of location or banking status."
    },
    {
      icon: "💡",
      title: "Innovation",
      description: "Combining traditional wisdom with modern technology to create better financial solutions for communities."
    },
  ];

  return (
    <div className="bg-white">
      <Navbar isAuthenticated={isAuthenticated} />
      {/* Hero Section */}
      <div className="relative bg-gray-700">
        <div className="absolute inset-0">
          <img
            className="w-full h-full object-cover opacity-20"
            src="https://images.unsplash.com/photo-1521791136064-7986c2920216?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80"
            alt="Community hands together"
          />
        </div>
        <div className="relative max-w-7xl mx-auto py-24 px-4 sm:py-32 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl font-extrabold tracking-tight text-white sm:text-5xl lg:text-6xl">
            About Ajo App
          </h1>
          <p className="mt-6 text-xl text-gray-100 max-w-3xl mx-auto">
            Revolutionizing traditional savings through technology, trust, and
            community.
          </p>
        </div>
      </div>

      {/* Our Story Section */}
      <div className="py-16 bg-white overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="lg:grid lg:grid-cols-2 lg:gap-8 items-center">
            <div className="mt-12 lg:mt-0">
              <h2 className="text-2xl font-medium text-gray-900 sm:text-4xl">
                Our Story
              </h2>
              <div className="mt-6 text-gray-500 space-y-6">
                <p className="text-lg">
                  Ajo App was born from a simple idea to modernize the
                  traditional savings culture rooted in trust and community. We
                  set out to build a platform that brings Esusu into the digital
                  age, making it easier, safer, and more transparent for
                  individuals and groups to save, support one another, and grow
                  together.
                </p>
                <p className="text-lg">
                  Today, Ajo App empowers communities with tools that simplify
                  savings and unlock new possibilities for financial growth.
                  What began as a solution for local savings groups has grown
                  into a platform transforming how communities approach
                  collective finance across multiple countries.
                </p>
              </div>
            </div>
            <div className="relative lg:ml-8">
              <img
                className="relative rounded-lg shadow-xl"
                src="https://images.unsplash.com/photo-1522202176988-66273c2fd55f?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80"
                alt="Team working together"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Founders Section */}
      <div className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-semibold text-gray-900">
              Our Vision
            </h2>
            <p className="mt-4 max-w-3xl text-lg text-gray-600 mx-auto">
              Built by a team passionate about financial inclusion, Ajo App represents our collective commitment 
              to democratizing savings and empowering communities worldwide. We're driven by a shared belief that 
              everyone deserves access to secure, transparent financial tools.
            </p>
          </div>

          <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-4 mt-12">
            {visionPoints.map((point) => (
              <div key={point.title} className="text-center bg-white rounded-lg p-6 shadow-sm hover:shadow-md transition-shadow">
                <div className="text-5xl mb-4">{point.icon}</div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">
                  {point.title}
                </h3>
                <p className="text-sm text-gray-600 leading-relaxed">
                  {point.description}
                </p>
              </div>
            ))}
          </div>

          <div className="mt-12 text-center">
            <p className="text-base text-gray-600 max-w-3xl mx-auto">
              Our diverse team brings together expertise in technology, finance, security, and community 
              development. Together, we're building more than just an app—we're creating a movement that 
              transforms how communities save, grow, and thrive together.
            </p>
          </div>
        </div>
      </div>

      {/* Stats Section */}
      <div className="bg-gray-700">
        <div className="max-w-7xl mx-auto py-12 px-4 sm:py-16 sm:px-6 lg:px-8 lg:py-20">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl font-extrabold text-white sm:text-4xl">
              Your financial growth is our priority
            </h2>
          </div>
          <div className="mt-10 text-center sm:max-w-3xl sm:mx-auto sm:grid sm:grid-cols-4 sm:gap-8">
            {stats.map((stat) => (
              <div key={stat.label} className="flex flex-col p-4">
                <dt className="order-2 mt-2 text-lg font-medium text-gray-200">
                  {stat.label}
                </dt>
                <dd className="order-1 text-4xl font-extrabold text-white">
                  {stat.value}
                </dd>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Mission Section */}
      <div className="py-16 bg-white overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="lg:grid lg:grid-cols-2 lg:gap-8 items-center">
            <div className="relative lg:mr-8 order-last">
              <img
                className="relative rounded-lg shadow-xl"
                src="https://images.unsplash.com/photo-1552664730-d307ca884978?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80"
                alt="Mobile app interface"
              />
            </div>
            <div className="mt-12 lg:mt-0">
              <h2 className="text-2xl font-medium text-gray-900 sm:text-4xl">
                Our Mission
              </h2>
              <div className="mt-6 text-gray-500 space-y-6">
                <p className="text-lg">
                  We're on a mission to democratize access to financial tools
                  that help communities thrive. By digitizing traditional
                  savings methods, we're creating opportunities for more people
                  to participate in collective finance safely and conveniently.
                </p>
                <p className="text-lg">
                  Our platform bridges the gap between informal savings circles
                  and formal financial services, offering the best of both
                  worlds - the trust and community of traditional systems with
                  the security and convenience of modern technology.
                </p>
              </div>
              <div className="mt-8">
                <Link
                  to="/community"
                  className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-gray-600 hover:bg-gray-700"
                >
                  Join Our Community
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default AboutUs;
